import { useState } from "react";
import type { FormEvent } from "react";
import { ArrowLeft, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../../backend/api/auth";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import logo from "../login/CT_Logo.png";

export const ResetPassword = (): JSX.Element => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errorText, setErrorText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleResetPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");
    setErrorText("");
    setSubmitting(true);

    try {
      const { error } = await authApi.requestPasswordReset(email.trim());
      if (error) throw error;
      setMessage(
        "If this email is eligible, a password reset link has been sent. Check your inbox and spam folder.",
      );
    } catch {
      setErrorText("We couldn't send the reset link. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-svh flex-col items-center justify-center bg-muted px-4 py-10 sm:px-6">
      <div className="flex w-full max-w-md flex-col gap-8">
        <img
          src={logo}
          alt="Claymore Thistle"
          className="mx-auto h-16 w-auto sm:h-20"
        />

        <Card>
          <CardHeader className="space-y-1.5 pb-5 text-center">
            <CardTitle className="text-2xl font-bold">
              {message ? "Check your inbox" : "Forgot password?"}
            </CardTitle>
            <CardDescription className="text-balance">
              {message
                ? "Follow the link in your email to choose a new password."
                : "Re-enter your email address and we’ll send you a password reset link."}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {message ? (
              <div role="status" className="space-y-5">
                <p className="text-sm text-muted-foreground">{message}</p>
                <Button className="w-full" onClick={() => navigate("/login")}>
                  Back to sign in
                </Button>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleResetPassword} aria-busy={submitting}>
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email address</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="reset-email"
                      type="email"
                      autoComplete="email"
                      placeholder="name@example.com"
                      disabled={submitting}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                      className="!pl-12"
                    />
                  </div>
                </div>

                {errorText && (
                  <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {errorText}
                  </div>
                )}


                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? "Sending..." : "Send reset link"}
                </Button>

                <Button
                  type="button"
                  variant="link"
                  className="w-full"
                  onClick={() => navigate("/login")}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default ResetPassword;
